/**
 * Shared Organization + WebSite JSON-LD for public marketing pages.
 *
 * Both nodes carry a stable `@id` so per-page schemas (SoftwareApplication,
 * FAQPage, Article…) can reference the same entities instead of describing
 * slightly different copies of the brand. Spread `ORG_WEBSITE_JSONLD` into a
 * route's `head().scripts` array.
 */

export const SITE_URL = "https://missionready.dev";
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

const ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: "MissionReady",
  alternateName: "Mission Ready",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/icon-512.png`,
    width: 512,
    height: 512,
  },
  image: `${SITE_URL}/og-cover.png`,
  description:
    "All-in-one security operations platform for guard, patrol, and field teams: dispatch, GPS tracking, incident reporting, checkpoints, scheduling, billing, and reporting.",
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "sales@missionready.dev",
      url: `${SITE_URL}/contact`,
      availableLanguage: ["en"],
    },
  ],
} as const;

const WEBSITE = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "MissionReady",
  url: SITE_URL,
  inLanguage: "en",
  publisher: { "@id": ORGANIZATION_ID },
} as const;

export const ORG_WEBSITE_JSONLD = [
  { type: "application/ld+json", children: JSON.stringify(ORGANIZATION) },
  { type: "application/ld+json", children: JSON.stringify(WEBSITE) },
];

/**
 * Product-level JSON-LD for SaaS marketing pages. Emits a SoftwareApplication
 * node tied to the shared Organization so crawlers resolve one product and one
 * publisher across the site.
 */
export function softwareApplicationJsonLd(opts: {
  /** Product / module name, e.g. "MissionReady Dispatch". */
  name: string;
  description: string;
  /** Absolute canonical URL of the page describing it. */
  url: string;
  featureList?: string[];
}) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: opts.name,
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Security operations software",
      operatingSystem: "Web, iOS, Android",
      description: opts.description,
      url: opts.url,
      image: `${SITE_URL}/og-cover.png`,
      brand: { "@id": ORGANIZATION_ID },
      publisher: { "@id": ORGANIZATION_ID },
      isPartOf: { "@id": WEBSITE_ID },
      ...(opts.featureList ? { featureList: opts.featureList } : {}),
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        url: `${SITE_URL}/pricing`,
        availability: "https://schema.org/InStock",
        description: "Free plan available, plus a 30-day free trial on paid plans. No credit card required.",
      },
    }),
  };
}
